import React, { useEffect, useState } from 'react';
import DialogModal from '../../../components/DialogModal';
import { RoundedButton } from '../styled';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import DropdownSelect from '../../../components/DropdownSelect';
import {
  ConcessionAmountTypeOptions,
  ConcessionTypeOptions,
  OptionalConcessionParticularOptions
} from '../utils/constant';
import { Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { SnackbarSeverityType } from '../../../enums';
import { OptionalConcessionMaster } from '../types';
import optionalConcession, {
  OptionalConcessionMasterRequest
} from '../../../apis/optionalConcession';

const OptionalConcessionMasterModal = ({
  isOptionalConcessionModalOpen,
  heading,
  buttonTitle,
  mode,
  fetchOptionalConcessionMaster,
  selectedConcessionData,
  onClose,
  showToast
}: {
  isOptionalConcessionModalOpen: boolean;
  heading: string;
  buttonTitle: string;
  mode: 'add' | 'edit';
  fetchOptionalConcessionMaster: () => void;
  selectedConcessionData?: OptionalConcessionMaster;
  onClose: () => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}) => {
  const [concessionName, setConcessionName] = useState<string>('');
  const [concessionType, setConcessionType] = useState<string>('DEPENDENT');
  const [applicableOn, setApplicableOn] = useState<string>('');
  const [type, setType] = useState<string>('percentage');
  const [amount, setAmount] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const buildOptionalConcessionPayload =
    (): OptionalConcessionMasterRequest => {
      const basePayload = {
        school: loggedInUser.school,
        concessionName,
        applicableOn: [applicableOn],
        concession:
          type === 'percentage'
            ? { percentage: amount || null, amount: null }
            : { percentage: null,amount: amount || null },
        type: concessionType
      };

      return mode === 'edit' && selectedConcessionData?.id
        ? { ...basePayload, id: selectedConcessionData.id }
        : basePayload;
    };

  const getErrorMessage = (error: any): string => {
    return (
      error?.errors?.[0]?.message ||
      `Error ${mode === 'add' ? 'Creating' : 'Updating'} Optional Concession`
    );
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildOptionalConcessionPayload();
      await optionalConcession.upsertSchoolOptionalConcessionMaster(
        loggedInUser.school,
        payload
      );
      showToast(
        `Successfully ${mode === 'add' ? 'created' : 'updated'}
           Optional Concession Master`,
        SnackbarSeverityType.success
      );
      reset();
      fetchOptionalConcessionMaster();
    } catch (error: any) {
      showToast(getErrorMessage(error), SnackbarSeverityType.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    onClose();
  };
  const reset = () => {
    setConcessionName('');
    setConcessionType('DEPENDENT');
    setApplicableOn('');
    setType('percentage');
    setAmount(0);
    handleModalClose();
  };

  useEffect(() => {
    if (mode === 'add') {
      setConcessionName('');
      setConcessionType('DEPENDENT');
      setApplicableOn('');
      setType('percentage');
      setAmount(0);
    } else if (mode === 'edit' && selectedConcessionData) {
      console.log(selectedConcessionData);
      setConcessionName(selectedConcessionData.concessionName);
      setConcessionType(selectedConcessionData.type);
      setApplicableOn(selectedConcessionData.applicableOn?.[0] ?? '');
      if (selectedConcessionData.concession['percentage']) {
        setType('percentage');
        setAmount(selectedConcessionData.concession.percentage ?? 0);
      } else {
        setType('amount');
        setAmount(selectedConcessionData.concession.amount ?? 0);
      }
    }
  }, [mode, selectedConcessionData]);

  return (
    <>
      <DialogModal
        open={isOptionalConcessionModalOpen}
        onClose={handleModalClose}
        title={heading}
        maxWidth={'sm'}
        actions={
          <RoundedButton
            onClick={handleSave}
            disabled={
              isSubmitting ||
              !concessionName ||
              !applicableOn ||
              amount < 0 ||
              isNaN(amount)
            }
          >
            {isSubmitting ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              buttonTitle
            )}
          </RoundedButton>
        }
      >
        <Box display='flex' flexDirection='column' gap={2}>
          <TextInputWithIcon
            label='Concession Name'
            name='concessionName'
            type='text'
            value={concessionName}
            onChange={(e) => setConcessionName(e.target.value)}
          />

          <DropdownSelect
            label='Concession Type'
            name='concessionType'
            value={concessionType}
            onChange={(e) => setConcessionType(e.target.value as string)}
            options={ConcessionTypeOptions}
          />
          <DropdownSelect
            label='Applicable On'
            name='applicableOn'
            value={applicableOn}
            onChange={(e) => setApplicableOn(e.target.value as string)}
            options={OptionalConcessionParticularOptions}
          />
          <DropdownSelect
            label='Type'
            name='type'
            value={type}
            onChange={(e) => {
              const newType = e.target.value as string;
              setType(newType);
              const newValue = selectedConcessionData?.concession[
              newType === 'percentage' ? 'percentage' : 'amount'
              ] ?? 0; 
              setAmount(newValue);
            }}
            options={ConcessionAmountTypeOptions}
          />
          <TextInputWithIcon
            label={type === 'percentage' ? 'Percentage' : 'Amount'}
            name={type === 'percentage' ? 'percentage' : 'amount'}
            type='number'
            value={amount}
            onChange={(e) =>
              setAmount((prev: number) => {
                if (parseInt(e.target.value) < 0) {
                  return prev;
                }
                return parseInt(e.target.value);
              })
            }
          />
        </Box>
      </DialogModal>
    </>
  );
};

export default OptionalConcessionMasterModal;
